import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { color } from '@shared/constants/Styles';

/**
 * Typography.tsx
 * This file contains the bare minimum typography settings.
 * All core typography settings should be defined here.
 * This is raw typography, no padding or margins should be defined here.
 * For extended typography for content see ContentTypography.tsx
 */
export const TextSize80 = css`
    font-size: 0.75rem;
    line-height: 1.5;
`;
export const TextSize90 = css`
    font-size: 0.875rem;
    line-height: 1.5;
`;
export const TextSize100 = css`
    font-size: 1rem;
    line-height: 1.5;
`;
export const TextSize200 = css`
    font-size: 1.2rem;
    line-height: 1.5;
`;
export const TextSize300 = css`
    font-size: 1.44rem;
    line-height: 1.15;
`;
export const TextSize400 = css`
    font-size: 1.728rem;
    line-height: 1.15;
`;
export const TextSize500 = css`
    font-size: 2.074rem;
    line-height: 1.15;
`;
export const TextSize600 = css`
    font-size: 2.488rem;
    line-height: 1.15;
`;
export const TextSize700 = css`
    font-size: 2.986rem;
    line-height: 1.15;
`;
export const TextSize800 = css`
    font-size: 3.25rem;
    line-height: 1.15;
`;

// MIXINS
export const MixinTruncate = css`
    display: -webkit-box;
    word-break: break-all;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const MixinTypographyLightSmall = css`
    font-weight: 300;
    letter-spacing: 0.03rem;
    ${TextSize100}
`;

export const MixinTypographyBodyMedium = css`
    font-weight: 400;
    letter-spacing: 0.03rem;
    ${TextSize100}
`;
export const MixinTypographyLightXSmall = css`
    font-weight: 300;
    letter-spacing: 0.03rem;
    ${TextSize90}
`;

export const MixinTypographyLightSubtitle = css`
    font-weight: 300;
    ${TextSize80}
`;

export const MixinTypographyDisplayMedium = css`
    font-weight: 900;
    ${TextSize700}
`;

export const MixinTypographyDisplaySmall = css`
    font-weight: 900;
    ${TextSize600}
`;
export const MixinTypographyDisplayXXSmall = css`
    font-weight: 800;
    letter-spacing: 0.03rem;
    ${TextSize200}
`;

export const MixinTypographyDisplayLarge = css`
    font-weight: 900;
    letter-spacing: -0.02rem;
    word-break: break-word;
    ${TextSize700}
`;

// DISPLAY
export const DisplayLarge = styled.h1`
    ${MixinTypographyDisplayLarge}
    color: ${(props) => props.theme.color.text};
`;
export const CenterDisplayLarge = styled(DisplayLarge)`
    text-align: center;
`;

export const DisplayMedium = styled.h2`
    ${MixinTypographyDisplayMedium}
    color: ${(props) => props.theme.color.text};
`;
export const CenterDisplayMedium = styled(DisplayMedium)`
    text-align: center;
    padding: 0.5rem 0 0 0;
`;

export const CenterDisplaySmall = styled.h3`
    ${MixinTypographyDisplaySmall}
    color: ${(props) => props.theme.color.text};
    text-align: center;
`;

export const DisplaySmall = styled.h3`
    ${MixinTypographyDisplaySmall}
    color: ${(props) => props.theme.color.text};
`;
export const DisplayXSmall = styled.h3`
    font-weight: 900;
    color: ${(props) => props.theme.color.text};
    ${TextSize400}
`;

export const DisplayXXSmall = styled.h3`
    ${MixinTypographyDisplayXXSmall}
    color: ${(props) => props.theme.color.text};
`;

// TITLE
export const TitleLarge = styled.h1`
    font-weight: 700;
    color: ${(props) => props.theme.color.text};
    ${TextSize500}
`;
export const TitleMedium = styled.h2`
    font-weight: 700;
    color: ${(props) => props.theme.color.text};
    ${TextSize400}
`;
export const TitleSmall = styled.h3`
    font-weight: 700;
    color: ${(props) => props.theme.color.text};
    ${TextSize200}
`;

export const TruncateTitleSmall = styled(TitleSmall)`
    ${MixinTruncate}
`;

export const TitleXSmall = styled.p`
    font-weight: 700;
    color: ${(props) => props.theme.color.text};
    ${TextSize100}
`;

export const TitleXXSmall = styled.p`
    font-weight: 700;
    color: ${(props) => props.theme.color.text};
    ${TextSize90}
`;

// LABEL
export const LabelXLarge = styled.p`
    font-weight: 500;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize300}
`;

export const LabelLarge = styled.p`
    font-weight: 500;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize200}
`;
export const LabelMedium = styled.p`
    font-weight: 500;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize100}
`;
export const LabelSmall = styled.p`
    font-weight: 500;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize90}
`;

// BODY
export const BodyXLarge = styled.p`
    font-weight: 400;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize500}
`;

export const BodyLarge = styled.p`
    font-weight: 400;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize300}
`;
export const BodyMedium = styled.p`
    font-weight: 400;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize200}
`;
export const BodySmall = styled.p`
    font-weight: 400;
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.313rem; // 21px
    ${TextSize100}
`;

export const BodyXSmall = styled.p`
    font-weight: 400;
    color: ${(props) => props.theme.color.textBody};
    ${TextSize90}
`;

export const LightXXLarge = styled.p`
    font-weight: 300;
    color: ${(props) => props.theme.color.textBody};
    letter-spacing: 0.03rem;
    ${TextSize500}
`;
export const LightXLarge = styled.p`
    font-weight: 300;
    font-size: 1.565rem; // 25px
    color: ${(props) => props.theme.color.textBody};
    line-height: 2.5625rem; // 41px
    letter-spacing: 0.03rem;
    ${TextSize400}
`;

export const LightLarge = styled.p`
    font-weight: 300;
    font-size: 1.3125rem; // 21px
    color: ${(props) => props.theme.color.textBody};
    line-height: 2rem; // 32px
    letter-spacing: 0.03rem;
    ${TextSize300}
`;
export const LightMedium = styled.p`
    font-weight: 300;
    font-size: 1.125rem; // 18px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.625rem; // 26px
    letter-spacing: 0.03rem;
    ${TextSize100}
`;

export const LightSmall = styled.p`
    ${MixinTypographyLightSmall}
    color: ${(props) => props.theme.color.textBody};
`;

export const LightXSmall = styled.p`
    ${MixinTypographyLightXSmall}
    color: ${(props) => props.theme.color.textBody};
`;

export const LightSubtitle = styled.p`
    ${MixinTypographyLightSubtitle}
    color: ${(props) => props.theme.color.textBody};
`;

export const SubtitleText = styled.p`
    font-weight: 200;
    text-align: center;
    ${TextSize100}
`;

export const CommandText = styled.p`
    font-family: 'Courier New', Courier, monospace;
    color: ${(props) => props.theme.color.text};
    letter-spacing: -0.02rem;

    ${TextSize80}
`;

export const CommandTextBadge = styled.span`
    font-family: 'Courier New', Courier, monospace;
    color: ${(props) => props.theme.color.text};
    ${TextSize80}
    background-color: ${(props) => props.theme.color.cardHover};
    border-radius: 0.25rem;
    padding: 0.125rem 0.25rem;
`;

// CHIP
export const ChipSmall = styled(LabelSmall)`
    color: ${color.success};
    border: 1px solid ${color.success};
    background-color: #e1e6e1;
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
`;
export const ChipMedium = styled(LabelSmall)`
    color: ${color.success};
    border: 1px solid ${color.success};
    background-color: #e1e6e1;
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
`;
